/* eslint-disable react-hooks/exhaustive-deps */
import { toast } from "react-toastify";
import Button from "../../components/Button/Button";
import InputField from "../../components/InputField/InputField";
import { getRoutePath } from "routes/route-utils";
import { AdyenRoutesName } from "routes/route-names";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Marketplace, MarketplaceFilter, PermissionEnum } from "TypesAndInterfaces";
import { getMarketplacesQuery } from "./queries";
import { enableMarketplaceMutation } from "./mutations";
import DisableForm from "components/DisableForm/DisableForm";
import usePermission from "hooks/usePermission";
import { displayError } from "utils/displayNotification";
import Checkbox from "components/Checkbox/Checkbox";
import ToggleButton from "components/ToggleButton/ToggleButton";
import DataTable, { TableColumn } from "react-data-table-component";
import checkmark from "../../assets/images/icons/checkmark.svg";
import notification from "../../assets/images/icons/notification.svg";

const Marketplaces = () => {
  //#region hooks
  const navigate = useNavigate();
  const [filterEnabledChecked, setFilterEnabledChecked] = useState(true);
  const [filterDisabledChecked, setFilterDisabledChecked] = useState(true);
  const [filterValidatedChecked, setFilterValidatedChecked] = useState(true);
  const [filterInvalidatedChecked, setFilterInvalidatedChecked] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [numberOfMarketplacesPerPage, setNumberOfMarketplacesPerPage] =
    useState(10);
  const [initialData, setInitialData] = useState<Marketplace[]>([]);
  const [data, setData] = useState(initialData);
  const [
    marketplacesToDisplayPerPage,
    setMarketplacesToDisplayPerPageDisplayPerPage,
  ] = useState<Marketplace[]>([]);
  const [totalNumberOfMarketplaces, setTotalNumberOfMarketplaces] = useState(0);
  const [marketplaceToDisable, setMarketplaceToDisable] =
    useState<Marketplace>();
  const [isDisableMarketplaceModalOpened, setIsDisableMarketplaceModalOpened] =
    useState(false);
  const [index, setIndex] = useState(1);
  const { isAuthorized: canManageShops } = usePermission(
    PermissionEnum.MANAGE_SHOPS
  );
  const { isAuthorized: canViewLogs } = usePermission(PermissionEnum.VIEW_LOGS);

  const [filtersAreDisabled, setFiltersAreDisabled] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  //#endregion

  //#region API CALLS
  const [getMarketplaces, { loading }] = useLazyQuery<
    { getMarketplaces: { marketplaces: Marketplace[]; totalCount: number } },
    {
      pagination: { size: number; index: number };
      filter?: { enabled?: boolean; searchText?: string; validated?: boolean };
    }
  >(getMarketplacesQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplaces) => {
      if (dataMarketplaces) {
        setFiltersAreDisabled(false);
        const { marketplaces, totalCount } = dataMarketplaces?.getMarketplaces;
        setMarketplacesToDisplayPerPageDisplayPerPage(marketplaces);
        setInitialData(initialData.concat(marketplaces));
        setTotalNumberOfMarketplaces(totalCount);
      }
    },
    onError: (error) => {
      setFiltersAreDisabled(false);
      displayError(`Something went wrong ${error.message}`);
    },
  });

  const [enableMarketplace] = useMutation(enableMarketplaceMutation, {
    onCompleted: () => {
      toast.info(`Marketplace enabled.`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });
  //#endpoint

  const handleSearch = debounce((searchValue: string) => {
    setSearchText(searchValue);
    fetchMarketplaces(searchValue);
  }, 500);

  const fetchMarketplaces = async (searchValue?: string) => {
    setFiltersAreDisabled(true);
    let filter: MarketplaceFilter = {
      enabled: true,
      validated: true,
      searchText: ""
    };
    const value = searchValue !== undefined ? searchValue : searchText;

    if ((filterEnabledChecked && !filterDisabledChecked) || (!filterEnabledChecked && filterDisabledChecked)) {
      filter = { enabled: filterEnabledChecked, searchText: value };
    } else if ((filterValidatedChecked && !filterInvalidatedChecked) || (!filterValidatedChecked && filterInvalidatedChecked)) {
      filter = { validated: filterValidatedChecked, searchText: value };
    } else if (filterEnabledChecked && filterValidatedChecked && !filterInvalidatedChecked && !filterDisabledChecked) {
      filter = { enabled: filterEnabledChecked, validated: filterValidatedChecked, searchText: value };
    } else if (filterEnabledChecked && filterValidatedChecked && filterInvalidatedChecked && filterDisabledChecked) {
      filter = { searchText: value };
    } else {
      filter = { searchText: value };
    }

    await getMarketplaces({
      variables: {
        pagination: { size: numberOfMarketplacesPerPage, index },
        filter,
      },
    });
  };

  const filterData = () => {
    if (!filterEnabledChecked && !filterDisabledChecked && !filterValidatedChecked && !filterInvalidatedChecked) {
      setInitialData([]);
      setData([]);
    } else {
      fetchMarketplaces();
    }
  };

  const toggleStatus = async (row: Marketplace) => {
    if (row.disabled) {
      await enableMarketplace({ variables: { id: row.id } });
      fetchMarketplaces();
    } else {
      setIsDisableMarketplaceModalOpened(true);
      setMarketplaceToDisable(row);
    }
  };

  useEffect(() => {
    setData(marketplacesToDisplayPerPage);
  }, [marketplacesToDisplayPerPage]);

  useEffect(() => {
    filterData();
  }, [filterEnabledChecked, filterDisabledChecked, filterValidatedChecked, filterInvalidatedChecked]);

  useEffect(() => {
    fetchMarketplaces();
  }, [index, numberOfMarketplacesPerPage]);

  useEffect(() => {
    if (marketplaceToDisable) {
      setIsDisableMarketplaceModalOpened(true);
    }
  }, [marketplaceToDisable]);

  const resetFilters = () => {
    setFilterEnabledChecked(true);
    setFilterDisabledChecked(true);
    setFilterValidatedChecked(true);
    setFilterInvalidatedChecked(true);
    setIndex(0); // to update the table
    setIndex(1);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const columnsMarketplaces: TableColumn<Marketplace>[] = [
    {
      name: "ID",
      selector: (row: Marketplace) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "NAME",
      selector: (row: Marketplace) => row.name,
      sortable: true,
    },
    {
      name: "VALIDATED",
      selector: (row: Marketplace) => {
        return row.validated ? (
          <img src={checkmark} alt="checkmark" />
        ) : (
          <img src={notification} alt="notification" />
        );
      },
      sortable: true,
    },
    {
      name: "STATUS",
      selector: (row: Marketplace) => {
        return (
          <ToggleButton
            checked={!row.disabled}
            onChange={() => toggleStatus(row)}
            className={""}
            name={""}
            value={""}
            checkedText={"enabled"}
            uncheckedText={"disabled"}
          />
        );
      },
      sortable: true,
    } as any,
    {
      name: "ACTIONS",
      cell: (row: Marketplace) => (
        <>
          {canManageShops ? (
            <Button
              icon="pencil"
              type="button"
              children={undefined}
              className={"button--circle"}
              onClick={() =>
                navigate({
                  pathname: getRoutePath(AdyenRoutesName.EDIT_MARKETPLACE),
                  search: `${createSearchParams({
                    marketplaceId: row.id.toString(),
                  })}`,
                })
              }
              content={undefined}
            />
          ) : (
            <div />
          )}
          {canViewLogs ? (
            <Button
              icon="logs"
              type="button"
              children={undefined}
              className={"button--circle"}
              onClick={() =>
                navigate({
                  pathname: getRoutePath(AdyenRoutesName.MARKETPLACE_LOGS),
                  search: `${createSearchParams({
                    marketplaceId: row.id.toString(),
                    name: row.name,
                  })}`,
                })
              }
              content={undefined}
            />
          ) : (
            <div />
          )}
        </>
      ),
    },
  ];

  const getColumnsMarketplaces = () => {
    if (!canManageShops && !canViewLogs)
      columnsMarketplaces.length = columnsMarketplaces.length - 1;
    return columnsMarketplaces;
  };

  const changePage = (page: number, totalRows: any) => {
    setIndex(page);
  };

  const changeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setNumberOfMarketplacesPerPage(currentRowsPerPage);
    setIndex(currentPage);
  };

  return (
    <div className="content-wrapper">
      <DisableForm
        dataType="Marketplace"
        isDisableModalOpened={isDisableMarketplaceModalOpened}
        dataToDisable={marketplaceToDisable!}
        setIsDisableModalOpened={setIsDisableMarketplaceModalOpened}
        fetchFunction={fetchMarketplaces}
      />
      <main className="main-container-wrapper">
        <section className="main-container">
          <header className="main-container__header">
            <h2 className="main-container__title">Marketplaces</h2>
            <div className="main-container__search-container">
              <InputField
                type="search"
                icon="search"
                className="no-margin"
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Enter your search term"
              />
            </div>
          </header>
          <div className="main-content">
            <aside className="main-content__sidebar">
              <header className="main-content__sidebar__header">
                <h4 className="main-content__sidebar__title">Filters</h4>
                <Button
                  className="button--small button--rounded button--no-wrap"
                  onClick={() => resetFilters()}
                  type="button"
                  content="RESET ALL"
                ></Button>
              </header>
              <div className="filters">
                <h5 className="filters__title">Status</h5>
                <Checkbox
                  className="filters__checkbox"
                  label="Enabled"
                  checked={filterEnabledChecked}
                  onChange={() =>
                    setFilterEnabledChecked(!filterEnabledChecked)
                  }
                  disabled={filtersAreDisabled}
                />
                <Checkbox
                  className="filters__checkbox"
                  label="Disabled"
                  checked={filterDisabledChecked}
                  onChange={() =>
                    setFilterDisabledChecked(!filterDisabledChecked)
                  }
                  disabled={filtersAreDisabled}
                />
                <Checkbox
                  className="filters__checkbox"
                  label="Validated"
                  checked={filterValidatedChecked}
                  onChange={() =>
                    setFilterValidatedChecked(!filterValidatedChecked)
                  }
                  disabled={filtersAreDisabled}
                />
                <Checkbox
                  className="filters__checkbox"
                  label="Invalidated"
                  checked={filterInvalidatedChecked}
                  onChange={() =>
                    setFilterInvalidatedChecked(!filterInvalidatedChecked)
                  }
                  disabled={filtersAreDisabled}
                />
              </div>
            </aside>
            <div className="main-content__result">
              {loading ? (
                <div className="adyen-loader" />
              ) : (
                <DataTable
                  pagination
                  paginationServer
                  paginationTotalRows={totalNumberOfMarketplaces}
                  paginationPerPage={numberOfMarketplacesPerPage}
                  onChangePage={changePage}
                  onChangeRowsPerPage={changeRowsPerPage}
                  columns={getColumnsMarketplaces()}
                  data={data}
                  paginationDefaultPage={index}
                  paginationResetDefaultPage={resetPaginationToggle}
                />
              )}
            </div>
          </div>
          <div className="bottom-button-container">
            {canManageShops ? (
              <Button
                className={"button--icon-only button--add"}
                type="button"
                content={"+"}
                icon={""}
                onClick={() =>
                  navigate(
                    getRoutePath(AdyenRoutesName.DASHBOARD_FIRST_MARKETPLACE)
                  )
                }
              />
            ) : (
              <div />
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Marketplaces;
